import type { Demo, DemoContent, Category } from './types'
import type { UpgradeReason } from './components/modals/UpgradeModal'

export class UpgradeRequiredError extends Error {
  reasons: UpgradeReason[]
  constructor(reasons: UpgradeReason[]) {
    super('Upgrade required')
    this.name = 'UpgradeRequiredError'
    this.reasons = reasons
  }
}

export class AuthRequiredError extends Error {
  constructor() {
    super('Authentication required')
    this.name = 'AuthRequiredError'
  }
}

function requireAuth(res: Response): void {
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth-modal:open', { detail: { mode: 'login' } }))
    throw new AuthRequiredError()
  }
}

const csrfToken = () =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''

export async function fetchDemo(id: string): Promise<Demo> {
  const res = await fetch(`/api/v1/demos/${id}`, {
    credentials: 'same-origin',
  })
  if (!res.ok) throw new Error('Failed to fetch demo')
  return res.json()
}

export async function publishDemo(id: string): Promise<Demo> {
  const res = await fetch(`/api/v1/demos/${id}/publish`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-CSRF-Token': csrfToken(),
    },
  })
  requireAuth(res)
  if (!res.ok) throw new Error('Failed to publish demo')
  return res.json()
}

export async function remixDemo(id: string): Promise<Demo> {
  const res = await fetch(`/api/v1/demos/${id}/remix`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-CSRF-Token': csrfToken(),
    },
  })
  requireAuth(res)
  if (!res.ok) throw new Error('Failed to remix demo')
  return res.json()
}

export async function fetchCategories(): Promise<Category[]> {
  const res = await fetch('/api/v1/categories', {
    credentials: 'same-origin',
  })
  if (!res.ok) throw new Error('Failed to fetch categories')
  return res.json()
}

export async function publishDemoWithCategories(
  id: string,
  categoryIds: number[]
): Promise<Demo> {
  const res = await fetch(`/api/v1/demos/${id}/publish`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify({ category_ids: categoryIds }),
  })
  requireAuth(res)
  if (res.status === 403) {
    const data = await res.json()
    if (data.error === 'upgrade_required') {
      throw new UpgradeRequiredError(data.reasons)
    }
  }
  if (!res.ok) throw new Error('Failed to publish demo')
  return res.json()
}

export async function uploadThumbnail(id: string, base64Image: string): Promise<Demo> {
  const res = await fetch(`/api/v1/demos/${id}/thumbnail`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify({ image: base64Image }),
  })
  requireAuth(res)
  if (!res.ok) throw new Error('Failed to upload thumbnail')
  return res.json()
}

export async function updateDemo(
  id: string,
  data: { title?: string; description?: string; content?: DemoContent; published?: boolean }
): Promise<Demo> {
  const body: Record<string, unknown> = {}
  if (data.title !== undefined) body.title = data.title
  if (data.description !== undefined) body.description = data.description
  if (data.content !== undefined) body.content = JSON.stringify(data.content)
  if (data.published !== undefined) body.published = data.published

  const res = await fetch(`/api/v1/demos/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify({ demo: body }),
  })
  requireAuth(res)
  if (!res.ok) throw new Error('Failed to update demo')
  return res.json()
}
